import { useEffect, useState } from 'react';
import { Box, Button, Flex } from '@chakra-ui/react';

import Select from '@components/filter/select';
import DatePickerComp from '@components/datePicker';
import FilterLoader from '@components/filterLoader';
import SelectedFilterList from './SelectedFilterList';
import { excludeKeys, checkFilterActive, dateFormatter } from '@helpers/utils';
import { ConditionalComponent } from '@components/functionComponent';
import useGetSchemeList from '@pages/ConsumerPromotion/hooks/query/useGetSchemeList';
import useGetStatusList from '@pages/ConsumerPromotion/hooks/query/useGetStatusList';
import { initialState, useFilters } from '@pages/ConsumerPromotion/hooks/state/useFilters';

export default function FilterList({ isOpen, onClose }) {
    const filters = useFilters();

    const [tempFilter, setTempFilter] = useState(initialState);

    const scheme = useGetSchemeList(filters.scheme);
    const status = useGetStatusList(filters.status);

    const hasFilters = excludeKeys(filters, ['searchParam', 'limit', 'sort', 'page']);
    const showSelectedFilter = checkFilterActive(hasFilters, initialState);

    const handleApplyFilter = () => {
        filters.set({
            status: tempFilter.status,
            scheme: tempFilter.scheme,
            dateStart: tempFilter.dateStart ? tempFilter.dateStart.format('DD MMM YYYY') : '',
            dateEnd: tempFilter.dateEnd ? tempFilter.dateEnd.format('DD MMM YYYY') : '',
        });
        onClose();
    };

    const handleResetFilter = (key) => {
        setTempFilter({ [key]: '' });
        filters.set({ [key]: '' });
    };

    const handleResetAllFilter = () => {
        setTempFilter(initialState);
        filters.reset();
        onClose();
    };

    const handleDateStart = (value) => {
        setTempFilter((prev) => ({ ...prev, dateStart: value }));
    };

    const handleDateEnd = (value) => {
        setTempFilter((prev) => ({ ...prev, dateEnd: value }));
    };

    const [showContent, setShowContent] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        let timer;
        if (isOpen) {
            setShowLoader(true);
            timer = setTimeout(() => {
                setShowContent(true);
                setShowLoader(false);
            }, 300);
        } else {
            setShowLoader(false);
            setShowContent(false);
        }
        return () => clearTimeout(timer);
    }, [isOpen]);

    return (
        <Box my={'24px'}>
            {showLoader && <FilterLoader />}

            {showContent && (
                <Box bg={'semantic.200'} p={'16px'} borderRadius={'8px'}>
                    <Flex gap={'24px'} justifyContent={'space-between'}>
                        <Flex wrap={'wrap'} gap={'24px'}>
                            <Select
                                title="Promo Scheme"
                                placeholder="3% - 8%"
                                width="240px"
                                selected={tempFilter?.scheme}
                                isGetAll={true}
                                refetchData={scheme?.refetch}
                                data={scheme?.data?.listScheme}
                                isLoading={scheme?.isLoading}
                                onSearch={(query) => setTempFilter({ ...tempFilter, scheme: query })}
                                onChangeFilter={(value) => setTempFilter({ ...tempFilter, scheme: value })}
                            />
                            <Select
                                title="Status"
                                placeholder="Pilih Status"
                                width="240px"
                                selected={tempFilter?.status}
                                isGetAll={true}
                                refetchData={status?.refetch}
                                data={status?.data}
                                isLoading={status?.isLoading}
                                onSearch={(query) => setTempFilter({ ...tempFilter, status: query })}
                                onChangeFilter={(value) => setTempFilter({ ...tempFilter, status: value })}
                            />
                            <DatePickerComp
                                width="240px"
                                isRange={false}
                                label="Start Date"
                                onChange={handleDateStart}
                                value={dateFormatter(tempFilter.dateStart)}
                                maxDate={tempFilter.dateEnd}
                                placeholder="Pilih Tanggal Mulai Promo"
                            />
                            <DatePickerComp
                                width="240px"
                                isRange={false}
                                label="End Date"
                                onChange={handleDateEnd}
                                value={dateFormatter(tempFilter.dateEnd)}
                                minDate={tempFilter.dateStart}
                                placeholder="Pilih Tanggal Akhir Promo"
                            />
                        </Flex>

                        <Flex alignSelf={'end'} gap={'12px'}>
                            <Button onClick={handleResetAllFilter} variant={'outline'}>
                                Reset All
                            </Button>
                            <Button onClick={handleApplyFilter}>Apply Filter</Button>
                        </Flex>
                    </Flex>
                </Box>
            )}

            <ConditionalComponent statement={showSelectedFilter && !isOpen}>
                <SelectedFilterList
                    filtersState={filters}
                    onRemoveFilterItem={(key) => handleResetFilter(key)}
                    onResetFilter={handleResetAllFilter}
                />
            </ConditionalComponent>
        </Box>
    );
}
